export const roleKeys = [
  'ADMINISTRATOR',
  'MANAGER',
  'DISPATCHER',
  'SALES_REPRESENTATIVE',
  'ACCOUNTING',
  'OPERATOR',
  'VIEWER',
  'FIELD_MARKER',
] as const

export type RoleKey = (typeof roleKeys)[number]

export const permissionKeys = [
  'dashboard.read',
  'lead.read',
  'lead.create',
  'lead.update',
  'lead.change_status',
  'lead.assign',
  'lead.archive',
  'lead.convert',
  'lead.delete',
  // Restauration d'une demande supprimée (`restore_lead_v1`, 16 sept. 2026) : « Annuler » de Demandes-V3.
  'lead.restore',
  'quote.read',
  'quote.create',
  'quote.update',
  'quote.send',
  'quote.accept',
  'client.read',
  'client.create',
  'client.update',
  'client.read_documents',
  'client.write_documents',
  'client.change_status',
  'client.notes_read',
  'client.notes_create',
  'client.notes_update',
  'client.notes_delete',
  'client.delete',
  'contract.read',
  'contract.measure',
  'contract.activate',
  'invoice.generate',
  'invoice.send',
  'contract.create',
  'contract.update',
  'contract.send',
  'contract.settings_manage',
  'contract.price_override',
  'contract.follow_up',
  'contract.cancel',
  'contract.delete',
  'contract.restore',
  'marker.read',
  'marker.update',
  'renewal_campaign.manage',
  'route.read',
  'route.create',
  'route.update',
  /** Module Secteurs (docs/secteurs/README.md §2.4) : déployées dans le frontend avant leur ajout en base. */
  'sector.read',
  'sector.edit',
  'sector.publish',
  /** Routes › Répartition (docs/routes/repartition/) : déployées dans le frontend avant leur ajout en base. */
  'dispatch.read',
  'dispatch.edit',
  'dispatch.publish',
  'mission.read',
  'mission.create',
  'mission.assign',
  'mission.supervise',
  'mission.mark_ready',
  'mission.start',
  'mission.pause',
  'mission.resume',
  'mission.complete',
  'invoice.read',
  'invoice.create',
  'invoice.issue',
  'invoice.update',
  'credit.read',
  'credit.create',
  'credit.issue',
  'credit.apply',
  'credit.cancel',
  'payment.read',
  'payment.record',
  /** Rattrapage chantier `audit-ui-v2` (item E) : document justificatif facultatif sur un paiement. */
  'payment.read_documents',
  'payment.write_documents',
  'employee.read',
  'employee.manage',
  'employee.assign',
  'equipment.read',
  'equipment.manage',
  'security_log.read',
  'settings.read',
  'settings.manage',
  'users.manage',
  'roles.manage',
  'feedback.create',
  'feedback.manage',
  'email.read',
  'email.manage',
  /** Alertes SMS (ADR-020) : déployées dans le frontend avant leur ajout en base. */
  'sms.read',
  'sms.reply',
  'sms.campaign',
  'sms.templates',
  'sms.consent_proof',
  'sms.settings',
  'sms.log',
  'assigned_mission.read',
  /** Module Rapports (ADR-023) : déployées dans le frontend avant leur ajout en base. */
  'report.read',
  'report.export_operational',
  'report.export_financial',
  'report.history_all',
  'report.delete',
  'backup.create',
] as const

export type PermissionKey = (typeof permissionKeys)[number]
/**
 * `PASSWORD_CHANGE_REQUIRED` : mot de passe temporaire pas encore remplacé. Le serveur n'accorde alors aucune
 * permission; seule la page de remplacement est accessible.
 */
export type UserStatus =
  'INVITED' | 'ACTIVE' | 'SUSPENDED' | 'DISABLED' | 'ARCHIVED' | 'PASSWORD_CHANGE_REQUIRED'

export interface AppSession {
  authUserId: string
  userId: string
  organizationId: string
  organizationName: string
  displayName: string
  email: string
  status: UserStatus
  roles: RoleKey[]
  permissions: PermissionKey[]
  employeeId?: string
  canOperate: boolean
}

export interface LoginCredentials {
  email: string
  password: string
}

export type AuthFailureCode =
  'INVALID_CREDENTIALS' | 'USER_INACTIVE' | 'ACCOUNT_SETUP_REQUIRED' | 'SESSION_EXPIRED' | 'UNEXPECTED'

export class AuthFailure extends Error {
  constructor(
    public readonly code: AuthFailureCode,
    message: string,
  ) {
    super(message)
    this.name = 'AuthFailure'
  }
}

export const roleLabels: Record<RoleKey, string> = {
  ADMINISTRATOR: 'Administrateur',
  MANAGER: 'Gestionnaire',
  DISPATCHER: 'Répartiteur',
  SALES_REPRESENTATIVE: 'Représentant commercial',
  ACCOUNTING: 'Comptabilité',
  OPERATOR: 'Opérateur',
  VIEWER: 'Consultation',
  FIELD_MARKER: 'Installateur de balises',
}
